import {SessionState} from '@github-ui/agent-sessions/types/session'
import {testIdProps} from '@github-ui/test-id-props'
import {AnchoredOverlay, RelativeTime} from '@primer/react'
import {clsx} from 'clsx'
import {type FocusEvent, memo, useCallback, useRef, useState} from 'react'

import styles from './agent-task-popover.module.css'

// Delay before showing the popover to prevent rapid tooltip flashing when hovering across rows
const POPOVER_SHOW_DELAY_MS = 50
// Delay before hiding the popover to allow mouse movement from anchor to overlay content
const POPOVER_HIDE_DELAY_MS = 200

export interface AgentTaskPopoverTaskItem {
  id?: string
  title?: string
  nwo?: string
  state: SessionState
  createdAt?: string
  lastUpdatedAt?: string
  href?: string
}

export interface AgentTaskPopoverProps {
  /** Array of tasks to display in the popover. */
  tasks: Array<AgentTaskPopoverTaskItem>
  children: React.ReactNode
  /** Optional className for the popover anchor wrapper div */
  className?: string
  /** When provided, clicking a task item calls this instead of navigating. */
  onTaskClick?: (taskId: string, e: React.MouseEvent) => void
}

function TaskItemContent({title, state, lastUpdatedAt}: {title?: string; state: SessionState; lastUpdatedAt?: string}) {
  const isQueued = state === SessionState.Queued
  const taskLastUpdatedAt = lastUpdatedAt

  return (
    <>
      {!isQueued && taskLastUpdatedAt && (
        <div className={styles.elapsedTime}>
          Updated <RelativeTime date={new Date(taskLastUpdatedAt)} />
        </div>
      )}
      {title && <div className={styles.title}>{title}</div>}
    </>
  )
}

function TaskListContent({
  tasks,
  showPopover,
  hidePopover,
  caretPosition,
  onTaskClick,
}: {
  tasks: Array<AgentTaskPopoverTaskItem>
  showPopover: () => void
  hidePopover: () => void
  caretPosition: 'bottomLeft' | 'bottomRight'
  onTaskClick?: (taskId: string, e: React.MouseEvent) => void
}) {
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      const relatedTarget = event.relatedTarget as HTMLElement | null
      if (!relatedTarget || !event.currentTarget.contains(relatedTarget)) {
        hidePopover()
      }
    },
    [hidePopover],
  )

  return (
    <div
      className={styles.popoverContainer}
      onMouseEnter={showPopover}
      onMouseLeave={hidePopover}
      onFocus={showPopover}
      onBlur={handleBlur}
      {...testIdProps('agent-task-popover')}
    >
      <div className={clsx(styles.taskList, caretPosition === 'bottomRight' ? styles.bottomRight : styles.bottomLeft)}>
        {tasks.map((task, index) => {
          const content = <TaskItemContent title={task.title} state={task.state} lastUpdatedAt={task.lastUpdatedAt} />
          const key = task.id ?? task.href ?? index
          if (task.href) {
            return (
              <a
                key={key}
                href={task.href}
                target="_blank"
                rel="noreferrer"
                className={styles.taskItem}
                onClick={
                  onTaskClick && task.id
                    ? (e: React.MouseEvent) => {
                        // task.id is guaranteed by the outer check
                        const id = task.id as string
                        onTaskClick(id, e)
                      }
                    : undefined
                }
              >
                {content}
              </a>
            )
          }
          return (
            <div key={key} className={styles.taskItem}>
              {content}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const AgentTaskPopover = memo(function AgentTaskPopover({
  tasks,
  children,
  className,
  onTaskClick,
}: AgentTaskPopoverProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [caretPosition, setCaretPosition] = useState<'bottomLeft' | 'bottomRight'>('bottomLeft')
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showPopover = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    if (showTimeoutRef.current) return
    showTimeoutRef.current = setTimeout(() => {
      showTimeoutRef.current = null
      setIsOpen(true)
    }, POPOVER_SHOW_DELAY_MS)
  }, [])

  const showPopoverImmediate = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }
    setIsOpen(true)
  }, [])

  const hidePopover = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, POPOVER_HIDE_DELAY_MS)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handlePositionChange = useCallback(({position}: {position: {anchorAlign: string}}) => {
    setCaretPosition(position.anchorAlign === 'end' ? 'bottomRight' : 'bottomLeft')
  }, [])

  if (!tasks || tasks.length === 0) {
    return <>{children}</>
  }

  return (
    <>
      <div
        ref={anchorRef}
        className={clsx(styles.popoverWrapper, className)}
        onMouseEnter={showPopover}
        onMouseLeave={hidePopover}
        onFocusCapture={showPopoverImmediate}
        onBlurCapture={hidePopover}
        {...testIdProps('agent-task-popover-wrapper')}
      >
        {children}
      </div>
      <AnchoredOverlay
        open={isOpen}
        renderAnchor={null}
        anchorRef={anchorRef}
        onClose={handleClose}
        onPositionChange={handlePositionChange}
        focusTrapSettings={{disabled: true}}
        side="outside-top"
        align="start"
        overlayProps={{
          preventFocusOnOpen: true,
          role: 'dialog',
          'aria-label': 'Agent tasks',
          overflow: 'visible',
          className: styles.overlay,
        }}
      >
        <TaskListContent
          tasks={tasks}
          showPopover={showPopover}
          hidePopover={hidePopover}
          caretPosition={caretPosition}
          onTaskClick={onTaskClick}
        />
      </AnchoredOverlay>
    </>
  )
})
